import { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ImobiliariaOnboardingDialog } from '@/components/contratacao/ImobiliariaOnboardingDialog';
import { ContratacaoCheckoutReview } from '@/components/contratacao/ContratacaoCheckoutReview';
import { getCheckoutOrderBumps } from '@/config/checkout-order-bumps';
import { Loader2, ArrowRight, Shield, Lock } from 'lucide-react';
import imobdeskLogo from '@/assets/imobdesk-logo.svg';
import { PublicFlowLayout } from '@/components/PublicFlowLayout';
import { PublicMotionMain } from '@/components/motion/PublicMotionMain';
import { publicFlowHeadingClass, publicFlowSubtitleClass } from '@/lib/imobdesk-public-theme';
import { cn } from '@/lib/utils';
import type { PlanoPublico } from '@/api/contratacao.actions.schema';
import {
  contratacaoOnboardingFormSchema,
  type ContratacaoOnboardingForm,
} from '@/api/contratacao.schema';

interface UrlQueryParams {
  ciclo?: 'mensal' | 'anual';
  trial?: number;
  card?: boolean;
  cupom?: string;
  nomeImobiliaria?: string;
  successUrl?: string;
  cancelUrl?: string;
}

type FormData = ContratacaoOnboardingForm;

/** Plano fictício — mesma forma que a API pública devolveria. */
const PLANO_MOCK: PlanoPublico = {
  id: 0,
  nome: 'Pro',
  slug: 'pro',
  precoMensal: 697,
  precoAnual: 6970,
  modulos: ['CRM e pipeline', 'Site da imobiliária', 'Relatórios e integrações'],
};

const PLANOS_MOCK: PlanoPublico[] = [
  {
    id: 1,
    nome: 'Basic',
    slug: 'basic',
    precoMensal: 297,
    precoAnual: 2970,
  },
  {
    id: 2,
    nome: 'Starter',
    slug: 'starter',
    precoMensal: 497,
    precoAnual: 4970,
  },
  PLANO_MOCK,
  {
    id: 4,
    nome: 'Premier',
    slug: 'premier',
    precoMensal: 997,
    precoAnual: 9970,
  },
];

const SecurityBadge = () => (
  <div
    className="flex flex-col items-center gap-3 mt-6 pt-4 border-t border-slate-200/50 animate-fade-in"
    style={{ animationDelay: '0.2s' }}
  >
    <div className="flex items-center justify-center gap-3">
      <div className="flex items-center gap-1.5 text-slate-600 text-xs bg-green-100 px-3 py-1.5 rounded-full">
        <Shield className="h-3.5 w-3.5 text-green-600" />
        <span>Dados protegidos</span>
      </div>
      <div className="flex items-center gap-1.5 text-slate-600 text-xs bg-green-100 px-3 py-1.5 rounded-full">
        <Lock className="h-3.5 w-3.5 text-green-600" />
        <span>Conexão segura</span>
      </div>
    </div>
    <p className="text-xs text-slate-400">Utilizamos Stripe.com para uma compra segura</p>
    <div className="text-center mt-2">
      <p className="text-sm text-slate-500 font-normal">
        ImobDesk Inteligência Imobiliária LTDA - CNPJ 55.080.461/0001-03
      </p>
    </div>
  </div>
);

const Logo = () => (
  <div className="flex justify-center mb-6 animate-fade-in relative z-10">
    <img src={imobdeskLogo} alt="ImobDesk" className="h-10" />
  </div>
);

const Header = ({ title, subtitle }: { title: string; subtitle: string }) => (
  <div className="text-center mb-6 animate-fade-in relative z-10" style={{ animationDelay: '0.1s' }}>
    <h1 className={cn(publicFlowHeadingClass, 'text-2xl sm:text-3xl mb-2')}>{title}</h1>
    <p className={cn(publicFlowSubtitleClass, 'text-base')}>{subtitle}</p>
  </div>
);

type PageWrapperProps = {
  children: React.ReactNode;
  motionKey: string;
  contentClassName?: string;
};

const PageWrapper = ({ children, motionKey, contentClassName }: PageWrapperProps) => (
  <PublicFlowLayout>
    <PublicMotionMain motionKey={motionKey} className={contentClassName}>
      {children}
    </PublicMotionMain>
  </PublicFlowLayout>
);

/**
 * Mesma UI da rota pública `/contratar/:slug` após login e plano carregado (sem links de troca de conta).
 * Sem chamadas à API: plano fixo; query string replica `?ciclo=&trial=&card=&cupom=&nomeImobiliaria=`.
 */
export default function ContratacaoFluxoPreview() {
  const [searchParams] = useSearchParams();

  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [urlParams, setUrlParams] = useState<UrlQueryParams>({});
  const [selectedCiclo, setSelectedCiclo] = useState<'mensal' | 'anual'>('mensal');
  const [cupomLocal, setCupomLocal] = useState('');
  const [selectedBumpPriceIds, setSelectedBumpPriceIds] = useState<string[]>([]);
  const orderBumps = useMemo(() => getCheckoutOrderBumps(), []);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [onboardingNomeError, setOnboardingNomeError] = useState<string | undefined>();
  const [urlQueryHydrated, setUrlQueryHydrated] = useState(false);
  const allowOnboardingAutoRef = useRef(true);

  const [formData, setFormData] = useState<FormData>({
    nomeImobiliaria: '',
    siteAtual: '',
  });

  const plano = PLANO_MOCK;

  const isTrial = urlParams.trial !== undefined && urlParams.trial > 0;
  const skipCicloChoice = !!urlParams.ciclo || isTrial;

  useEffect(() => {
    const params: UrlQueryParams = {};
    const cicloParam = searchParams.get('ciclo');
    if (cicloParam === 'mensal' || cicloParam === 'anual') {
      params.ciclo = cicloParam;
      setSelectedCiclo(cicloParam);
    }
    const trialParam = searchParams.get('trial');
    if (trialParam !== null && /^\d+$/.test(trialParam)) {
      const dias = parseInt(trialParam, 10);
      if (dias >= 0) {
        params.trial = dias;
        if (dias > 0) setSelectedCiclo('mensal');
      }
    }
    const cardParam = searchParams.get('card');
    if (cardParam !== null) {
      params.card = cardParam === 'true';
    }
    const cupomParam = searchParams.get('cupom');
    if (cupomParam) params.cupom = cupomParam;
    const nomeImobiliariaParam = searchParams.get('nomeImobiliaria');
    if (nomeImobiliariaParam) params.nomeImobiliaria = nomeImobiliariaParam;
    const successUrlParam = searchParams.get('successUrl');
    if (successUrlParam) params.successUrl = successUrlParam;
    const cancelUrlParam = searchParams.get('cancelUrl');
    if (cancelUrlParam) params.cancelUrl = cancelUrlParam;

    setUrlParams(params);
    setFormData((prev) => ({
      ...prev,
      nomeImobiliaria: params.nomeImobiliaria || prev.nomeImobiliaria,
    }));
    if (params.cupom) {
      setCupomLocal(params.cupom);
    }
    setUrlQueryHydrated(true);
  }, [searchParams]);

  useEffect(() => {
    if (!urlQueryHydrated || checkoutLoading) return;
    if (!allowOnboardingAutoRef.current) return;
    const nome = formData.nomeImobiliaria.trim();
    if (nome.length >= 2) {
      setOnboardingOpen(false);
      allowOnboardingAutoRef.current = false;
    } else {
      setOnboardingOpen(true);
    }
  }, [urlQueryHydrated, checkoutLoading, formData.nomeImobiliaria]);

  const formatPrice = (price: number | string) => {
    const value = typeof price === 'string' ? parseFloat(price) : price;
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const calculateDiscount = () => {
    if (!plano.precoAnual) return 0;
    const mensal = typeof plano.precoMensal === 'string' ? parseFloat(plano.precoMensal) : plano.precoMensal;
    const anual = typeof plano.precoAnual === 'string' ? parseFloat(plano.precoAnual) : plano.precoAnual;
    const valorAnualSemDesconto = mensal * 12;
    return Math.round(((valorAnualSemDesconto - anual) / valorAnualSemDesconto) * 100);
  };

  const calculateDiscountValue = () => {
    if (!plano.precoAnual) return 0;
    const mensal = typeof plano.precoMensal === 'string' ? parseFloat(plano.precoMensal) : plano.precoMensal;
    const anual = typeof plano.precoAnual === 'string' ? parseFloat(plano.precoAnual) : plano.precoAnual;
    return mensal * 12 - anual;
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleOnboardingContinue = () => {
    const result = contratacaoOnboardingFormSchema.safeParse(formData);
    if (!result.success) {
      const nomeErr = result.error.errors.find((e) => e.path[0] === 'nomeImobiliaria');
      setOnboardingNomeError(nomeErr?.message ?? 'Nome inválido');
      return;
    }
    setOnboardingNomeError(undefined);
    allowOnboardingAutoRef.current = false;
    setOnboardingOpen(false);
  };

  const handleSubmitContratacao = () => {
    const result = contratacaoOnboardingFormSchema.safeParse(formData);
    if (!result.success) {
      const nomeErr = result.error.errors.find((e) => e.path[0] === 'nomeImobiliaria');
      if (nomeErr) {
        setOnboardingNomeError(nomeErr.message);
        setOnboardingOpen(true);
      }
      return;
    }
    setOnboardingNomeError(undefined);
    setCheckoutLoading(true);
    window.setTimeout(() => {
      setCheckoutLoading(false);
    }, 2200);
  };

  if (checkoutLoading) {
    return (
      <PageWrapper motionKey="checkout-loading">
        <div className="flex flex-col items-center gap-4 relative z-10">
          <Logo />
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
          <p className="text-slate-500">Redirecionando para o pagamento...</p>
        </div>
      </PageWrapper>
    );
  }

  const desconto = calculateDiscount();
  const descontoValor = calculateDiscountValue();
  const trialDays = urlParams.trial ?? 0;
  const title = isTrial
    ? `Teste grátis por ${trialDays} dia${trialDays !== 1 ? 's' : ''}`
    : 'Finalizar assinatura';
  const precoMensalAnual = plano.precoAnual
    ? (typeof plano.precoAnual === 'string' ? parseFloat(plano.precoAnual) : plano.precoAnual) / 12
    : null;

  return (
    <>
      <PageWrapper motionKey="contratar-unico" contentClassName="max-w-lg w-full">
        <Logo />
        <Header
          title={title}
          subtitle='Confira o plano, utilize cupoms e clique em "Continuar"'
        />

        <div className="max-w-lg w-full relative z-10 space-y-6 animate-fade-in">
        <ContratacaoCheckoutReview
          plano={plano}
          selectedCiclo={selectedCiclo}
          onSelectCiclo={setSelectedCiclo}
          skipCicloChoice={skipCicloChoice}
          urlCiclo={urlParams.ciclo}
          isTrial={isTrial}
          trialDays={urlParams.trial}
          trialCardRequired={urlParams.card}
          descontoPct={desconto}
          descontoValor={descontoValor}
          formatPrice={formatPrice}
          precoMensalAnual={precoMensalAnual}
          cupom={cupomLocal}
          onCupomChange={setCupomLocal}
          cupomFromUrl={urlParams.cupom}
          bumps={orderBumps}
          selectedBumpPriceIds={selectedBumpPriceIds}
          onToggleBump={(stripePriceId, selected) => {
            setSelectedBumpPriceIds((prev) => {
              if (selected) return prev.includes(stripePriceId) ? prev : [...prev, stripePriceId];
              return prev.filter((id) => id !== stripePriceId);
            });
          }}
          onAlterarImobiliaria={() => {
            setOnboardingNomeError(undefined);
            setOnboardingOpen(true);
          }}
          nomeImobiliaria={formData.nomeImobiliaria}
          siteAtual={formData.siteAtual}
          planosDisponiveis={PLANOS_MOCK}
          currentPlanoSlug={plano.slug}
          isLoadingPlanosDisponiveis={false}
          onSelecionarPlano={() => {}}
        />

        <div className="space-y-3">
          <p className="text-xs text-slate-400 text-center font-medium">
            Ao continuar você aceita os{' '}
            <a
              href="https://imobdesk.com.br/termos-de-uso"
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 hover:text-indigo-700 underline"
            >
              termos de uso e licença
            </a>{' '}
            e a{' '}
            <a
              href="https://imobdesk.com.br/privacidade"
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 hover:text-indigo-700 underline"
            >
              política de privacidade
            </a>{' '}
            da ImobDesk
          </p>

          <Button
            className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white text-lg transition-all duration-200 hover:scale-[1.02]"
            onClick={handleSubmitContratacao}
          >
            {isTrial ? 'Iniciar teste grátis' : 'Ir para o pagamento'}
            <ArrowRight className="h-5 w-5 ml-2" />
          </Button>

          <SecurityBadge />
        </div>
      </div>
      </PageWrapper>

      <ImobiliariaOnboardingDialog
        open={onboardingOpen}
        isTrial={isTrial}
        nomeImobiliaria={formData.nomeImobiliaria}
        siteAtual={formData.siteAtual}
        onNomeChange={(value) => {
          handleInputChange('nomeImobiliaria', value);
          setOnboardingNomeError(undefined);
        }}
        onSiteChange={(value) => handleInputChange('siteAtual', value)}
        nomeError={onboardingNomeError}
        onContinue={handleOnboardingContinue}
      />
    </>
  );
}
